import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getSchoolConfig } from '../../utils/domainConfig';
import { FaArrowLeft, FaShieldAlt, FaUserLock, FaDatabase, FaTrashAlt, FaEnvelope, FaPhone } from 'react-icons/fa';
import '../../styles/PrivacyPolicy.css';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const schoolConfig = getSchoolConfig();

  return (
    <div className="privacy-policy-container">
      <div className="privacy-header">
        <button className="back-btn-privacy" onClick={() => navigate('/')}>
          <FaArrowLeft /> Back to Home
        </button>
        <div className="header-content-privacy">
          <FaShieldAlt className="header-icon-privacy" />
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: December 28, 2024</p>
        </div>
      </div>

      <div className="privacy-content">
        {/* Introduction */}
        <section className="policy-section">
          <h2><FaUserLock /> Introduction</h2>
          <p>
            {schoolConfig.schoolName} ("we," "our," or "the School") is committed to protecting the privacy and security 
            of personal information of our students, parents, teachers, and staff. This Privacy Policy outlines 
            how we collect, use, store, and protect personal data in compliance with applicable data protection 
            laws and regulations.
          </p>
          <p>
            By enrolling your child at {schoolConfig.schoolName} or by accepting employment with us, you acknowledge that 
            you have read and understood this Privacy Policy and consent to the collection and use of your 
            personal information as described herein.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="policy-section">
          <h2><FaDatabase /> Information We Collect</h2>
          <p>We collect and process the following types of personal information:</p>
          
          <div className="info-category">
            <h3>Student Information</h3>
            <ul>
              <li>Full name, date of birth, and gender</li>
              <li>Parent/guardian names and contact information (mobile numbers, email addresses)</li>
              <li>Home address and emergency contact details</li>
              <li>Academic records, including class, roll number, and examination results</li>
              <li>Attendance records and disciplinary information</li>
              <li>Fee payment history and financial records</li>
              <li>Medical information relevant to school activities</li>
              <li>Photographs and video recordings for school events and documentation</li>
            </ul>
          </div>

          <div className="info-category">
            <h3>Teacher and Staff Information</h3>
            <ul>
              <li>Full name, date of birth, and contact information</li>
              <li>Professional qualifications and employment history</li>
              <li>Attendance and performance records</li>
              <li>Salary and payment information</li>
              <li>Emergency contact details</li>
            </ul>
          </div>
        </section>

        {/* How We Use Information */}
        <section className="policy-section">
          <h2>How We Use Your Information</h2>
          <p>We use the collected personal information for the following purposes:</p>
          <ul>
            <li><strong>Educational Administration:</strong> Managing student enrollment, academic progress, attendance tracking, and examinations</li>
            <li><strong>Communication:</strong> Sending important notices, updates, fee reminders, and academic information to parents and guardians via SMS, WhatsApp, email, or phone calls</li>
            <li><strong>Financial Management:</strong> Processing fee payments, maintaining financial records, and generating receipts</li>
            <li><strong>Safety and Security:</strong> Ensuring student safety, managing emergency situations, and maintaining school security</li>
            <li><strong>Regulatory Compliance:</strong> Fulfilling legal obligations and reporting requirements as mandated by educational authorities</li>
            <li><strong>School Operations:</strong> Managing daily operations, staff management, and institutional planning</li>
          </ul>
        </section>

        {/* Data Sharing - Important Section */}
        <section className="policy-section highlight-section">
          <h2>Data Sharing and Disclosure</h2>
          <div className="important-notice">
            <p className="emphasis">
              <strong>We do not share, sell, rent, or disclose personal information to third parties, 
              except as specifically outlined below:</strong>
            </p>
          </div>
          
          <div className="sharing-category">
            <h3>Authorized Data Sharing:</h3>
            <ul>
              <li>
                <strong>Central Board of Secondary Education (CBSE):</strong> We share necessary student 
                information with CBSE for registration, examination, and certification purposes as required 
                by regulatory mandates.
              </li>
              <li>
                <strong>Government Authorities:</strong> We may disclose information when legally required 
                by law enforcement agencies, courts, or other governmental bodies.
              </li>
              <li>
                <strong>Service Providers:</strong> We may engage trusted third-party service providers 
                (such as WhatsApp Business API for communication) who are contractually obligated to 
                maintain confidentiality and use data only for specified purposes.
              </li>
            </ul>
          </div>

          <p className="assurance">
            We ensure that any third-party service providers adhere to strict data protection standards 
            and use personal information solely for the purposes we authorize.
          </p>
        </section>

        {/* WhatsApp Communication */}
        <section className="policy-section">
          <h2>WhatsApp Business Communication</h2>
          <p>
            We use WhatsApp Business API to send important notifications, fee receipts, and school updates 
            to parents and guardians. By providing your mobile number, you consent to receive communications 
            via WhatsApp.
          </p>
          <p>Key points regarding WhatsApp communication:</p>
          <ul>
            <li>Messages are sent only for school-related purposes (fee notifications, receipts, important announcements)</li>
            <li>Your mobile number is used solely for this communication and is not shared with other parties</li>
            <li>You can opt out of WhatsApp notifications by contacting the school office</li>
            <li>WhatsApp communication complies with Meta's Business API policies and data protection standards</li>
          </ul>
        </section>

        {/* Data Security */}
        <section className="policy-section">
          <h2>Data Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect personal 
            information against unauthorized access, disclosure, alteration, or destruction:
          </p>
          <ul>
            <li>Secure database systems with encrypted storage</li>
            <li>Access controls and user authentication for staff accessing personal data</li>
            <li>Regular security audits and system updates</li>
            <li>Staff training on data protection and confidentiality</li>
            <li>Physical security measures for paper-based records</li>
          </ul>
          <p>
            While we take reasonable precautions to protect your information, no method of electronic 
            storage or transmission is 100% secure. We cannot guarantee absolute security but remain 
            committed to protecting your data to the best of our ability.
          </p>
        </section>

        {/* Data Retention */}
        <section className="policy-section">
          <h2>Data Retention</h2>
          <p>
            We retain personal information only for as long as necessary to fulfill the purposes outlined 
            in this policy and to comply with legal, regulatory, or institutional requirements:
          </p>
          <ul>
            <li><strong>Student Records:</strong> Maintained during enrollment and for a reasonable period after the student leaves the school for academic record-keeping and alumni purposes</li>
            <li><strong>Financial Records:</strong> Retained as per accounting and tax regulations (typically 7-10 years)</li>
            <li><strong>Teacher Records:</strong> Maintained during employment and for legally mandated periods thereafter</li>
          </ul>
        </section>

        {/* Your Rights - Important Section */}
        <section className="policy-section highlight-section">
          <h2><FaTrashAlt /> Your Rights and Data Control</h2>
          <p>You have the following rights regarding your personal information:</p>
          
          <div className="rights-list">
            <div className="right-item">
              <h3>Right to Access</h3>
              <p>You can request access to the personal information we hold about you or your child.</p>
            </div>

            <div className="right-item">
              <h3>Right to Correction</h3>
              <p>You can request correction of inaccurate or incomplete personal information.</p>
            </div>

            <div className="right-item">
              <h3>Right to Data Portability</h3>
              <p>You can request a copy of your personal data in a structured, commonly used format.</p>
            </div>

            <div className="right-item important-right">
              <h3>Right to Deletion</h3>
              <p>
                <strong>Parents and guardians have the right to request deletion of their child's personal 
                information after the child has left the school.</strong> Upon receiving a deletion request, 
                we will:
              </p>
              <ul>
                <li>Verify the identity of the requester</li>
                <li>Assess legal and regulatory obligations for data retention</li>
                <li>Delete or anonymize the data within a reasonable timeframe (typically 30-60 days)</li>
                <li>Provide written confirmation of deletion</li>
              </ul>
              <p className="note">
                Please note that certain records may need to be retained for compliance with legal 
                obligations (such as financial records for tax purposes) or legitimate institutional 
                interests (such as alumni records with consent).
              </p>
            </div>

            <div className="right-item">
              <h3>Right to Object</h3>
              <p>You can object to certain processing activities, such as marketing communications (if any).</p>
            </div>
          </div>
        </section>

        {/* Children's Privacy */}
        <section className="policy-section">
          <h2>Children's Privacy</h2>
          <p>
            {schoolConfig.schoolName} is committed to protecting the privacy of children. We collect and process 
            student information only with parental consent and for legitimate educational purposes. 
            Parents have the right to review, correct, or request deletion of their child's information 
            at any time.
          </p>
        </section>

        {/* Changes to Policy */}
        <section className="policy-section">
          <h2>Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices, 
            legal requirements, or operational needs. We will notify parents, students, and staff of 
            any material changes through:
          </p>
          <ul>
            <li>School notice boards and circulars</li>
            <li>Email and WhatsApp notifications</li>
            <li>Updates on the school website</li>
          </ul>
          <p>
            Continued enrollment or employment after policy changes constitutes acceptance of the 
            updated Privacy Policy.
          </p>
        </section>

        {/* Contact Information */}
        <section className="policy-section contact-section">
          <h2><FaEnvelope /> Contact Us</h2>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or the 
            handling of your personal information, please contact us:
          </p>
          
          <div className="contact-details">
            <div className="contact-item-privacy">
              <FaEnvelope className="contact-icon-privacy" />
              <div>
                <h4>Email</h4>
                <a href={`mailto:${schoolConfig.email}`}>{schoolConfig.email}</a>
              </div>
            </div>

            <div className="contact-item-privacy">
              <FaPhone className="contact-icon-privacy" />
              <div>
                <h4>Phone</h4>
                {schoolConfig.phones.map((phone, i) => (
                  <span key={phone}>{i > 0 && ' | '}<a href={`tel:+91${phone}`}>+91 {phone}</a></span>
                ))}
              </div>
            </div>

            <div className="contact-item-privacy">
              <FaShieldAlt className="contact-icon-privacy" />
              <div>
                <h4>Data Protection Officer</h4>
                <p>{schoolConfig.schoolName} School Office</p>
                <p>Office Hours: Monday to Saturday, 8:00 AM - 3:00 PM</p>
              </div>
            </div>
          </div>

          <div className="response-time">
            <p>
              <strong>Response Time:</strong> We aim to respond to all privacy-related inquiries 
              within 7-10 business days.
            </p>
          </div>
        </section>

        {/* Acknowledgment */}
        <section className="policy-section acknowledgment-section">
          <h2>Acknowledgment</h2>
          <p>
            By enrolling your child at {schoolConfig.schoolName} or by accepting employment with us, you acknowledge 
            that you have read, understood, and agree to be bound by this Privacy Policy. You consent to 
            the collection, use, storage, and disclosure of your personal information as described in this 
            policy.
          </p>
          <p className="signature-note">
            For any concerns or to exercise your rights under this policy, please contact us using the 
            information provided above.
          </p>
        </section>
      </div>

      {/* Footer */}
      <div className="privacy-footer">
        <p>© 2024 {schoolConfig.schoolName}. All rights reserved.</p>
        <p>UDISE Code: 10051803224 | Registration No: 20710942024929180129</p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
